import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { SecretsService } from '@generated/services/secrets.service';
import { SecretResult } from '@generated/models/secret-result';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { SECRET_FIELDS } from '@core/assistant/assistant-catalog';

@Component({
  selector: 'app-secret-edit',
  standalone: true,
  imports: [
    AssistantActionDirective,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    ToolbarModule,
    TooltipModule,
    DeleteConfirmationComponent
],
  templateUrl: './secret-edit.html',
  styleUrl: './secret-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SecretEditComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = SECRET_FIELDS;

  private secretsService = inject(SecretsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  secretId: string | null = null;
  secret: SecretResult | null = null;
  isLoading = false;
  isSaving = false;
  isNew = false;

  // Form fields
  name = '';
  value = '';

  // Delete dialog state
  deleteDialogVisible = false;
  isDeleting = false;

  ngOnInit(): void {
    this.secretId = this.route.snapshot.paramMap.get('secretId');

    if (this.secretId) {
      this.loadSecret();
    } else {
      this.isNew = true;
      this.cdr.markForCheck();
    }
  }

  private loadSecret(): void {
    if (!this.secretId) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.secretsService.apiSecretsIdGet({ id: this.secretId }).subscribe({
      next: (secret) => {
        this.secret = secret;
        this.name = secret.name || '';
        this.value = secret.value || '';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load secret:', error);
        this.notificationService.showError('Failed to load secret');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  save(): void {
    if (!this.name || !this.value) {
      this.notificationService.showError('Name and value are required');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    if (this.isNew) {
      // Create new secret
      this.secretsService.apiSecretsPost({
        body: {
          name: this.name,
          value: this.value
        }
      }).subscribe({
        next: () => {
          this.notificationService.showSuccess('Secret created successfully');
          this.isSaving = false;
          this.router.navigate(['/settings']);
        },
        error: (error) => {
          console.error('Failed to create secret:', error);
          this.notificationService.showError('Failed to create secret');
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      // Update existing secret
      if (!this.secretId) return;

      this.secretsService.apiSecretsIdPut({
        id: this.secretId,
        body: {
          name: this.name,
          value: this.value
        }
      }).subscribe({
        next: () => {
          this.notificationService.showSuccess('Secret updated successfully');
          this.isSaving = false;
          this.router.navigate(['/settings']);
        },
        error: (error) => {
          console.error('Failed to update secret:', error);
          this.notificationService.showError('Failed to update secret');
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/settings']);
  }

  deleteSecret(): void {
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.secretId) return;

    this.isDeleting = true;
    this.cdr.markForCheck();

    this.secretsService.apiSecretsIdDelete({ id: this.secretId }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Secret deleted successfully');
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.router.navigate(['/settings']);
      },
      error: (error) => {
        console.error('Failed to delete secret:', error);
        this.notificationService.showError('Failed to delete secret');
        this.isDeleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.cdr.markForCheck();
  }
}
